import { createVNode, render, isVNode } from 'vue';

import MessageBoxConstructor from './index.vue';

import type { AppContext, ComponentPublicInstance, Slot } from 'vue';
import type {
  Action,
  EyMessageBoxOptions,
  IEyMessageBox,
  MessageBoxData,
  MessageBoxState
} from './message-box.type';

import _ from 'lodash-es';

// component default merge props & data

const messageInstance = new Map<
  ComponentPublicInstance<{ doClose: () => void }>, // marking doClose as function
  {
    options: any;
    resolve: (res: any) => void;
    reject: (reason?: any) => void;
  }
>();

const getAppendToElement = (props: any): HTMLElement => {
  let appendTo: HTMLElement = document.body;
  if (props.appendTo) {
    if (_.isString(props.appendTo)) {
      appendTo = document.querySelector<HTMLElement>(props.appendTo)!;
    }
    if (_.isElement(props.appendTo)) {
      appendTo = props.appendTo;
    }

    // should fallback to default value with a warning
    if (!_.isElement(appendTo)) {
      appendTo = document.body;
    }
  }
  return appendTo;
};

const initInstance = (
  props: any,
  container: HTMLElement,
  appContext: AppContext | null = null
) => {
  const vnode = createVNode(
    MessageBoxConstructor,
    props,
    _.isFunction(props.message) || isVNode(props.message)
      ? {
          default: _.isFunction(props.message)
            ? props.message
            : () => props.message
        }
      : null
  );
  vnode.appContext = appContext;
  render(vnode, container);
  getAppendToElement(props).appendChild(container.firstElementChild!);
  return vnode.component;
};

const genContainer = () => {
  return document.createElement('div');
};

const showMessage = (
  options: EyMessageBoxOptions,
  appContext?: AppContext | null
) => {
  const container = genContainer();
  // Adding destruct method.
  // when transition leaves emitting `vanish` evt. so that we can do the clean job.
  // 事件处理
  (options as any).onVanish = () => {
    // not sure if this causes mem leak, need proof to verify that.
    // maybe calling out like 1000 msg-box then close them all.
    render(null, container);
    messageInstance.delete(vm); // Remove vm to avoid mem leak.
    // here we were suppose to call document.body.removeChild(container.firstElementChild)
    // but render(null, container) did that job for us. so that we do not call that directly
  };

  // 事件处理
  (options as any).onAction = (action: Action) => {
    const currentMsg = messageInstance.get(vm);

    if (action === 'cancel' || action === 'close') {
      if (options.distinguishCancelAndClose && action !== 'cancel') {
        currentMsg?.reject('close');
      } else {
        currentMsg?.reject('cancel');
      }
    } else {
      currentMsg?.resolve(action);
    }
  };

  const instance = initInstance(options, container, appContext)!;

  // This is how we use message box programmably.
  // Maybe consider releasing a template version?
  // get component instance like v2.
  const vm = instance.proxy as ComponentPublicInstance<
    {
      visible: boolean;
      doClose: () => void;
      title: string;
      header?: Slot;
      footer?: Slot;
    } & MessageBoxState
  >;

  for (const prop in options) {
    if (Object.hasOwn(options, prop) && !Object.hasOwn(vm.$props, prop)) {
      vm[prop as keyof ComponentPublicInstance] = (options as any)[prop];
    }
  }

  // change visibility after everything is settled
  vm.visible = true;
  return vm;
};

async function EyMessageBox(
  options: EyMessageBoxOptions,
  ready: (
    vm: ComponentPublicInstance<{
      doClose: () => void;
      title: string;
      header?: Slot;
      footer?: Slot;
    }>
  ) => void,
  appContext?: AppContext | null
): Promise<MessageBoxData>;
function EyMessageBox(
  options: EyMessageBoxOptions,
  ready: (
    vm: ComponentPublicInstance<{
      doClose: () => void;
      title: string;
      header?: Slot;
      footer?: Slot;
    }>
  ) => void,

  appContext: AppContext | null = null
) {
  return new Promise((resolve, reject) => {
    const vm = showMessage(
      options,
      appContext ?? (EyMessageBox as IEyMessageBox)._context
    );

    ready(vm);

    // collect this vm in order to handle upcoming events.
    messageInstance.set(vm, {
      options,
      resolve,
      reject
    });
  });
}

EyMessageBox.close = () => {
  // instance.setupInstall.doClose()
  // instance.setupInstall.state.visible = false

  messageInstance.forEach((_, vm) => {
    vm.doClose();
  });

  messageInstance.clear();
};
(EyMessageBox as IEyMessageBox)._context = null;

export default EyMessageBox as IEyMessageBox;

export { EyMessageBoxOptions };
